import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bookmark, Plus, SlidersHorizontal, Trash2, X } from 'lucide-react';
import {
  FILTER_QUERY_PARAM,
  VALUELESS_OPERATORS,
  filterDefinitionSchema,
  filterOperatorLabel,
  type FilterCondition,
  type FilterDefinition,
  type FilterEntityType,
  type FilterFieldDescriptor,
  type FilterOperator,
} from '@fint/validation';
import { api, ApiError } from '@/lib/api';
import { useActiveOrg, useAuthStore } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { Badge, Button, Input, Sheet, SheetBody, SheetFooter, SheetHeader } from '@/features/common/ui';

/**
 * The advanced filter: "invoices where customer is Acme and balance due is
 * more than ₹10,000 and due date is before today", one condition per row,
 * against the field catalogue the API publishes for the list.
 *
 * ── WHERE IT LIVES ──
 * In the URL, as `?where=<json>` or `?savedFilterId=<id>`, so a refresh
 * keeps it and "the unpaid invoices over ₹10,000 on the Acme project" can
 * be pasted to a colleague - which is the form that question gets asked in.
 * `useAdvancedFilter` is the one reader and writer; the page appends
 * `params` to its list request and `key` to its query key.
 *
 * ── THE SHEET EDITS A DRAFT ──
 * Nothing is applied until "Apply". Live-applying each keystroke would fire
 * a list request per character and, worse, would show a half-built
 * condition's results as though they were an answer.
 *
 * ── SAVED FILTERS ──
 * Book-level, offered only on the list they were built on. Applying one
 * puts its id in the URL; editing its conditions detaches into an ad-hoc
 * filter rather than quietly rewriting something a colleague also uses.
 */

/** What the page holds: an ad-hoc filter or a saved one, never both. */
export interface AdvancedFilterValue {
  definition: FilterDefinition | null;
  savedFilterId: string | null;
}

export const EMPTY_FILTER: AdvancedFilterValue = { definition: null, savedFilterId: null };

interface SavedFilter {
  id: string;
  name: string;
  entityType: string;
  definition: FilterDefinition;
  createdBy: string;
  createdByName: string | null;
}

/**
 * The query params for a list request. Nothing at all when no filter is
 * applied, so an unfiltered list URL stays exactly as it was.
 */
export function advancedFilterParams(value: AdvancedFilterValue): { where?: string; savedFilterId?: string } {
  if (value.savedFilterId) return { savedFilterId: value.savedFilterId };
  if (value.definition && value.definition.conditions.length > 0) return { where: JSON.stringify(value.definition) };
  return {};
}

/** The URL's `where` / `savedFilterId`, read and written in one place. */
export function useAdvancedFilter() {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(FILTER_QUERY_PARAM);
  const savedFilterId = searchParams.get('savedFilterId');

  const value = useMemo<AdvancedFilterValue>(() => {
    if (savedFilterId) return { definition: null, savedFilterId };
    if (!raw) return EMPTY_FILTER;
    try {
      const parsed = filterDefinitionSchema.safeParse(JSON.parse(raw));
      return parsed.success && parsed.data.conditions.length > 0 ? { definition: parsed.data, savedFilterId: null } : EMPTY_FILTER;
    } catch {
      // A hand-edited URL that does not parse is no filter, not a crash; the
      // sheet opens empty and the list is the whole list.
      return EMPTY_FILTER;
    }
  }, [raw, savedFilterId]);

  const set = useCallback(
    (next: AdvancedFilterValue) => {
      setSearchParams(
        (params) => {
          const out = new URLSearchParams(params);
          out.delete(FILTER_QUERY_PARAM);
          out.delete('savedFilterId');
          const p = advancedFilterParams(next);
          if (p.savedFilterId) out.set('savedFilterId', p.savedFilterId);
          else if (p.where) out.set(FILTER_QUERY_PARAM, p.where);
          return out;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Memoised on the value, which is memoised on the URL: a page can put
  // `params` in a dependency list without re-running on every render.
  const params = useMemo(() => advancedFilterParams(value), [value]);
  return {
    value,
    set,
    clear: () => set(EMPTY_FILTER),
    /** Spread into `qs({...})` on the list path. */
    params,
    /** One string for the query key: the same filter is the same cache entry. */
    key: params.savedFilterId ? `saved:${params.savedFilterId}` : (params.where ?? ''),
    active: !!params.savedFilterId || !!params.where,
  };
}

const ENTITY_NOUN: Record<FilterEntityType, string> = {
  invoice: 'invoices',
  expense: 'expenses',
  payment: 'payments',
  customer: 'customers',
  transaction: 'transactions',
};

export function AdvancedFilter({
  entityType,
  value,
  onChange,
}: {
  entityType: FilterEntityType;
  value: AdvancedFilterValue;
  onChange: (next: AdvancedFilterValue) => void;
}) {
  const org = useActiveOrg();
  const [open, setOpen] = useState(false);
  const active = !!value.savedFilterId || !!value.definition?.conditions.length;

  // The catalogue and the saved filters are fetched when the filter is in
  // use - to name the applied conditions under the button - or the sheet is
  // open; a page with no filter on it pays for neither.
  const fields = useQuery({
    queryKey: ['filter-fields', org?.id, entityType],
    queryFn: () => api.get<FilterFieldDescriptor[]>(`/filters/fields?entityType=${entityType}`),
    enabled: !!org?.id && (open || active),
    staleTime: 60_000,
  });
  const saved = useQuery({
    queryKey: ['saved-filters', org?.id, entityType],
    queryFn: () => api.get<{ items: SavedFilter[] }>(`/filters/saved?entityType=${entityType}`),
    enabled: !!org?.id && (open || !!value.savedFilterId),
    staleTime: 60_000,
  });

  const savedApplied = value.savedFilterId ? saved.data?.items.find((s) => s.id === value.savedFilterId) : undefined;
  const applied = savedApplied?.definition ?? value.definition;
  const count = applied?.conditions.length ?? (value.savedFilterId ? 1 : 0);
  const byKey = useMemo(() => new Map((fields.data ?? []).map((f) => [f.key, f])), [fields.data]);

  return (
    <div className="space-y-2" data-testid="advanced-filter">
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant={active ? 'secondary' : 'outline'} size="sm" onClick={() => setOpen(true)} aria-expanded={open} data-testid="advanced-filter-open">
          <SlidersHorizontal className="size-4" aria-hidden />
          {savedApplied ? savedApplied.name : 'Filter'}
          {count > 0 ? (
            <Badge variant="info" data-testid="advanced-filter-count">
              {count}
            </Badge>
          ) : null}
        </Button>
        {active ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(EMPTY_FILTER)} aria-label="Clear the filter" data-testid="advanced-filter-clear">
            <X className="size-4" aria-hidden />
            Clear
          </Button>
        ) : null}
      </div>

      {/* The applied conditions in words, each removable, so the filter is
          visible without opening anything - and "why is this list short" has
          its answer on the page. */}
      {applied && applied.conditions.length > 0 && fields.data ? (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="advanced-filter-chips">
          {applied.conditions.map((c, i) => (
            <span key={i} className="inline-flex max-w-full items-center gap-1 rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-xs" data-testid="filter-chip">
              <span className="truncate">{describeCondition(c, byKey.get(c.field))}</span>
              <button
                type="button"
                aria-label={`Remove: ${describeCondition(c, byKey.get(c.field))}`}
                className="rounded-full p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground touch:p-1.5"
                onClick={() => {
                  const conditions = applied.conditions.filter((_, idx) => idx !== i);
                  onChange(conditions.length ? { definition: { ...applied, conditions }, savedFilterId: null } : EMPTY_FILTER);
                }}
              >
                <X className="size-3" aria-hidden />
              </button>
              {i < applied.conditions.length - 1 ? <span className="sr-only">{applied.conjunction}</span> : null}
            </span>
          ))}
          {applied.conditions.length > 1 ? (
            <span className="text-xs text-muted-foreground">{applied.conjunction === 'or' ? 'any of these' : 'all of these'}</span>
          ) : null}
        </div>
      ) : null}

      {open ? (
        <FilterSheet
          entityType={entityType}
          fields={fields.data ?? []}
          loading={fields.isLoading}
          error={fields.error instanceof ApiError ? fields.error.message : fields.isError ? 'The fields could not be loaded.' : null}
          saved={saved.data?.items ?? []}
          value={value}
          applied={applied ?? null}
          onChange={onChange}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
}

function FilterSheet({
  entityType,
  fields,
  loading,
  error,
  saved,
  value,
  applied,
  onChange,
  onClose,
}: {
  entityType: FilterEntityType;
  fields: FilterFieldDescriptor[];
  loading: boolean;
  error: string | null;
  saved: SavedFilter[];
  value: AdvancedFilterValue;
  applied: FilterDefinition | null;
  onChange: (next: AdvancedFilterValue) => void;
  onClose: () => void;
}) {
  useEscapeToClose(onClose);
  const org = useActiveOrg();
  const perms = usePermissions();
  const userId = useAuthStore((s) => s.user?.id);
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<FilterDefinition>(() => applied ?? { conjunction: 'and', conditions: [] });
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const byKey = useMemo(() => new Map(fields.map((f) => [f.key, f])), [fields]);
  const noun = ENTITY_NOUN[entityType];

  const invalidateSaved = () => {
    void queryClient.invalidateQueries({ queryKey: ['saved-filters', org?.id, entityType] });
  };
  const create = useMutation({
    mutationFn: (name: string) => api.post<SavedFilter>('/filters/saved', { entityType, name, definition: draft }),
    onSuccess: (row) => {
      invalidateSaved();
      setSaving(false);
      setSaveName('');
      onChange({ definition: null, savedFilterId: row.id });
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/filters/saved/${id}`),
    onSuccess: (_r, id) => {
      invalidateSaved();
      if (value.savedFilterId === id) onChange(EMPTY_FILTER);
    },
  });

  const setCondition = (i: number, patch: Partial<FilterCondition>) =>
    setDraft((d) => ({ ...d, conditions: d.conditions.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) }));
  const addCondition = () => {
    const first = fields[0];
    if (!first) return;
    setDraft((d) => ({ ...d, conditions: [...d.conditions, { field: first.key, operator: first.operators[0], value: defaultValue(first, first.operators[0]) }] }));
  };
  const apply = () => {
    onChange(draft.conditions.length === 0 ? EMPTY_FILTER : { definition: draft, savedFilterId: null });
    onClose();
  };
  const createError = create.error instanceof ApiError ? create.error.message : null;
  const removeError = remove.error instanceof ApiError ? remove.error.message : null;
  const mayDelete = (s: SavedFilter) => s.createdBy === userId || perms.can('settings', 'manage');

  return (
    <Sheet label={`Filter ${noun}`} onClose={onClose} wide testId="filter-sheet">
      <SheetHeader title={`Filter ${noun}`} onClose={onClose} />
      <SheetBody>
        {saved.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Saved filters</p>
            <div className="flex flex-wrap gap-1.5">
              {saved.map((s) => (
                <span
                  key={s.id}
                  className={
                    value.savedFilterId === s.id
                      ? 'inline-flex items-center gap-1 rounded-full border border-primary bg-primary/10 px-2.5 py-1 text-xs'
                      : 'inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs'
                  }
                  data-testid="filter-saved-chip"
                >
                  <button
                    type="button"
                    className="hover:underline"
                    title={s.createdByName ? `Saved by ${s.createdByName}` : 'Saved for this book'}
                    onClick={() => {
                      onChange({ definition: null, savedFilterId: s.id });
                      onClose();
                    }}
                  >
                    {s.name}
                  </button>
                  {mayDelete(s) ? (
                    <button
                      type="button"
                      aria-label={`Delete the saved filter ${s.name}`}
                      className="rounded-full p-0.5 text-muted-foreground hover:text-destructive"
                      onClick={() => {
                        if (window.confirm(`Delete the saved filter "${s.name}" for everyone in this book?`)) remove.mutate(s.id);
                      }}
                    >
                      <Trash2 className="size-3" aria-hidden />
                    </button>
                  ) : null}
                </span>
              ))}
            </div>
            {removeError ? (
              <p role="alert" className="text-xs text-destructive">
                {removeError}
              </p>
            ) : null}
          </div>
        ) : null}

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading fields…</p>
        ) : error ? (
          <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        ) : (
          <div className="space-y-3">
            {draft.conditions.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="filter-empty">
                No conditions yet. Add one to narrow the list - each row is one thing that must be true.
              </p>
            ) : null}
            {draft.conditions.map((c, i) => (
              <ConditionRow
                key={i}
                index={i}
                condition={c}
                field={byKey.get(c.field)}
                fields={fields}
                conjunction={draft.conjunction}
                onConjunction={(conjunction) => setDraft((d) => ({ ...d, conjunction }))}
                onChange={(patch) => setCondition(i, patch)}
                onRemove={() => setDraft((d) => ({ ...d, conditions: d.conditions.filter((_, idx) => idx !== i) }))}
              />
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addCondition} disabled={fields.length === 0 || draft.conditions.length >= 25} data-testid="filter-add-condition">
              <Plus className="size-4" aria-hidden />
              Add a condition
            </Button>
          </div>
        )}

        {saving ? (
          <div className="flex flex-wrap items-end gap-2 rounded-md border border-border p-3">
            <div className="min-w-[12rem] flex-1">
              <Input
                label="Name this filter"
                autoFocus
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (saveName.trim()) create.mutate(saveName.trim());
                  }
                }}
                placeholder="e.g. Unpaid over 30 days"
                hint="Saved for everyone in this book. Only you or an admin can change it."
                data-testid="filter-save-name"
              />
            </div>
            <Button type="button" size="sm" onClick={() => saveName.trim() && create.mutate(saveName.trim())} disabled={!saveName.trim()} loading={create.isPending} data-testid="filter-save">
              Save
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setSaving(false)}>
              Cancel
            </Button>
            {createError ? (
              <p role="alert" className="w-full text-xs text-destructive">
                {createError}
              </p>
            ) : null}
          </div>
        ) : null}
      </SheetBody>
      <SheetFooter className="justify-between">
        <Button type="button" variant="ghost" size="sm" onClick={() => setSaving(true)} disabled={draft.conditions.length === 0 || saving} data-testid="filter-save-open">
          <Bookmark className="size-4" aria-hidden />
          Save for the book
        </Button>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setDraft({ conjunction: 'and', conditions: [] });
              onChange(EMPTY_FILTER);
              onClose();
            }}
            data-testid="filter-clear"
          >
            Clear
          </Button>
          <Button type="button" onClick={apply} data-testid="filter-apply">
            Apply
          </Button>
        </div>
      </SheetFooter>
    </Sheet>
  );
}

function ConditionRow({
  index,
  condition,
  field,
  fields,
  conjunction,
  onConjunction,
  onChange,
  onRemove,
}: {
  index: number;
  condition: FilterCondition;
  field?: FilterFieldDescriptor;
  fields: FilterFieldDescriptor[];
  conjunction: 'and' | 'or';
  onConjunction: (c: 'and' | 'or') => void;
  onChange: (patch: Partial<FilterCondition>) => void;
  onRemove: () => void;
}) {
  const select = 'min-h-9 rounded-md border border-input bg-background px-2 text-sm shadow-sm touch:min-h-11 touch:text-base';
  const valueless = VALUELESS_OPERATORS.includes(condition.operator);
  return (
    <div className="rounded-md border border-border p-3" data-testid="filter-condition">
      <div className="flex flex-wrap items-center gap-2">
        {/* The conjunction is one choice for the whole filter, shown on the
            second row where it reads as the join between two conditions. */}
        <div className="w-14 shrink-0 text-xs text-muted-foreground">
          {index === 0 ? (
            'Where'
          ) : index === 1 ? (
            <select aria-label="Join conditions with" value={conjunction} onChange={(e) => onConjunction(e.target.value as 'and' | 'or')} className="rounded border border-input bg-background px-1 py-0.5 text-xs" data-testid="filter-conjunction">
              <option value="and">and</option>
              <option value="or">or</option>
            </select>
          ) : (
            conjunction
          )}
        </div>
        <select
          aria-label="Field"
          value={condition.field}
          onChange={(e) => {
            const next = fields.find((f) => f.key === e.target.value);
            if (!next) return;
            // Operator and value reset with the field: "contains" makes no
            // sense once the field became a date, and a leftover value would
            // be refused with a message about a field the person is no longer
            // looking at.
            onChange({ field: next.key, operator: next.operators[0], value: defaultValue(next, next.operators[0]) });
          }}
          className={`${select} min-w-[10rem] flex-1`}
          data-testid="filter-field"
        >
          {fields.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Condition"
          value={condition.operator}
          onChange={(e) => {
            const operator = e.target.value as FilterOperator;
            onChange({ operator, value: field ? defaultValue(field, operator, condition.value) : undefined });
          }}
          className={`${select} min-w-[10rem] flex-1`}
          data-testid="filter-operator"
        >
          {(field?.operators ?? []).map((op) => (
            <option key={op} value={op}>
              {filterOperatorLabel(op, field?.type ?? 'text')}
            </option>
          ))}
        </select>
        <Button type="button" variant="ghost" size="icon" onClick={onRemove} aria-label="Remove this condition" data-testid="filter-remove">
          <X className="size-4" aria-hidden />
        </Button>
      </div>
      {!valueless && field ? (
        <div className="mt-2 pl-16">
          <ValueInput field={field} operator={condition.operator} value={condition.value} onChange={(value) => onChange({ value })} />
          {field.hint ? <p className="mt-1 text-xs text-muted-foreground">{field.hint}</p> : null}
        </div>
      ) : null}
    </div>
  );
}

/** The starting value for a field and operator, so a fresh row is already valid. */
function defaultValue(field: FilterFieldDescriptor, operator: FilterOperator, previous?: unknown): unknown {
  if (VALUELESS_OPERATORS.includes(operator)) return undefined;
  if (operator === 'between') return Array.isArray(previous) && previous.length === 2 ? previous : ['', ''];
  if (field.type === 'boolean') return typeof previous === 'boolean' ? previous : true;
  if (field.type === 'select' || field.type === 'multiselect') return Array.isArray(previous) ? previous : [];
  return typeof previous === 'string' ? previous : '';
}

function ValueInput({
  field,
  operator,
  value,
  onChange,
}: {
  field: FilterFieldDescriptor;
  operator: FilterOperator;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const input = 'min-h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm touch:min-h-11 touch:text-base';
  const type = field.type === 'date' ? 'date' : 'text';
  const mode = field.type === 'number' ? 'decimal' : undefined;

  if (operator === 'between') {
    const pair = Array.isArray(value) ? value : ['', ''];
    return (
      <div className="flex flex-wrap items-center gap-2">
        <input type={type} inputMode={mode} aria-label="From" value={String(pair[0] ?? '')} onChange={(e) => onChange([e.target.value, pair[1]])} className={`${input} w-40`} data-testid="filter-value" />
        <span className="text-xs text-muted-foreground">and</span>
        <input type={type} inputMode={mode} aria-label="To" value={String(pair[1] ?? '')} onChange={(e) => onChange([pair[0], e.target.value])} className={`${input} w-40`} data-testid="filter-value-to" />
      </div>
    );
  }

  if (field.type === 'boolean') {
    return (
      <select aria-label="Value" value={value === false ? 'no' : 'yes'} onChange={(e) => onChange(e.target.value === 'yes')} className={`${input} px-2`} data-testid="filter-value">
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    );
  }

  if (field.type === 'select' || field.type === 'multiselect') {
    // A picker that adds to a set, with the chosen as removable chips - the
    // same shape the tag filter uses, and one a thumb can work, which a
    // native multi-select is not.
    const chosen = Array.isArray(value) ? (value as string[]) : [];
    const options = field.options ?? [];
    const labelOf = (v: string) => options.find((o) => o.value === v)?.label ?? v;
    return (
      <div className="space-y-1.5">
        <select
          aria-label="Add a value"
          value=""
          onChange={(e) => {
            const v = e.target.value;
            if (v && !chosen.includes(v)) onChange([...chosen, v]);
          }}
          className={`${input} min-w-[12rem] px-2`}
          data-testid="filter-value"
        >
          <option value="">{chosen.length ? `${chosen.length} chosen - add another…` : 'Choose…'}</option>
          {options
            .filter((o) => !chosen.includes(o.value))
            .map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
        </select>
        {chosen.length ? (
          <div className="flex flex-wrap gap-1.5">
            {chosen.map((v) => (
              <span key={v} className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary" data-testid="filter-value-chip">
                {labelOf(v)}
                <button type="button" aria-label={`Remove ${labelOf(v)}`} onClick={() => onChange(chosen.filter((x) => x !== v))} className="rounded-full p-0.5 hover:bg-primary/20">
                  <X className="size-3" aria-hidden />
                </button>
              </span>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <input
      type={type}
      inputMode={mode}
      aria-label="Value"
      value={typeof value === 'string' ? value : ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.type === 'number' ? '0.00' : undefined}
      className={`${input} w-full max-w-sm`}
      data-testid="filter-value"
    />
  );
}

/** One condition in words, for the chips under the button. */
export function describeCondition(c: FilterCondition, field?: FilterFieldDescriptor): string {
  const label = field?.label ?? c.field;
  const op = filterOperatorLabel(c.operator, field?.type ?? 'text');
  if (VALUELESS_OPERATORS.includes(c.operator)) return `${label} ${op}`;
  const name = (v: unknown) => {
    const s = String(v);
    return field?.options?.find((o) => o.value === s)?.label ?? s;
  };
  if (Array.isArray(c.value)) {
    if (c.operator === 'between') return `${label} ${op} ${name(c.value[0])} and ${name(c.value[1])}`;
    return `${label} ${op} ${c.value.map(name).join(', ')}`;
  }
  if (typeof c.value === 'boolean') return `${label}: ${c.value ? 'yes' : 'no'}`;
  return `${label} ${op} ${name(c.value)}`;
}
